/**
 * Утилиты для работы с датами
 * 
 * Файл содержит вспомогательные функции для работы с датами,
 * включая расчет дат публикации и форматирование дат.
 */
import i18n from '../i18n/i18n';

/**
 * Получает следующую дату публикации
 * 
 * Учитывает выходные дни (суббота и воскресенье) и пропускает их.
 * Если предыдущая дата не указана, начинает от текущей даты.
 * 
 * @param lastPublishDate - Последняя дата публикации или null
 * @returns Следующая дата публикации
 */
export const getNextPublishDate = (lastPublishDate: Date | null): Date => {
    const date = lastPublishDate ? new Date(lastPublishDate) : new Date();
    date.setDate(date.getDate() + 1);

    // Пропускаем выходные
    while (date.getDay() === 0 || date.getDay() === 6) {
        date.setDate(date.getDate() + 1);
    }

    return date;
};

/**
 * Форматирует дату в локализованную строку
 * 
 * Преобразует дату в строку в локализованном формате
 * с указанием дня недели, числа, месяца и года.
 * 
 * @param date - Дата для форматирования
 * @returns Отформатированная строка даты
 */
export const formatDate = (date: Date): string => {
    return date.toLocaleDateString(i18n.t('dateFormat.locale'), {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

/**
 * Создает строку даты публикации в локальном часовом поясе пользователя
 * @param publishTime - время в формате HH:mm (например: "10:00")
 * @returns строка даты в формате YYYY-MM-DDTHH:mm:ss
 * 
 * Пример: createPublishAtString("10:00") -> "2025-01-15T10:00:00"
 */
export const createPublishAtString = (publishTime: string): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}T${publishTime}:00`;
};

/**
 * Создает строку даты публикации из даты и времени
 * @param publishDate - дата в формате YYYY-MM-DD (например: "2025-01-15")
 * @param publishTime - время в формате HH:mm (например: "10:00")
 * @returns строка даты в формате YYYY-MM-DD HH:MM
 * 
 * Пример: createPublishAtFromDateTime("2025-01-15", "10:00") -> "2025-01-15 10:00"
 */
export const createPublishAtFromDateTime = (publishDate: string, publishTime: string): string => {
    return `${publishDate} ${publishTime}`;
};

/**
 * Проверяет, должна ли тренировка быть опубликованной на основе времени publish_at
 * Время публикации абсолютное - если указано 10:00, то во всех часовых поясах публикация в 10:00
 * @param publishAt - время публикации в формате ISO string (например: "2025-08-25T14:31:17.583035Z")
 * @returns true если тренировка должна быть опубликована, false если запланирована
 * 
 * Примеры работы:
 * - Если сейчас 25 августа 2025, 09:30, а publish_at = "2025-08-25T10:00:00.000000Z" -> вернет false (еще не 10:00)
 * - Если сейчас 25 августа 2025, 10:00, а publish_at = "2025-08-25T10:00:00.000000Z" -> вернет true (ровно 10:00)
 * - Если сейчас 25 августа 2025, 10:30, а publish_at = "2025-08-25T10:00:00.000000Z" -> вернет true (уже 10:30)
 * - Если сейчас 26 августа 2025, 09:00, а publish_at = "2025-08-25T10:00:00.000000Z" -> вернет true (вчерашняя дата)
 * - Если сейчас 24 августа 2025, 23:00, а publish_at = "2025-08-25T10:00:00.000000Z" -> вернет false (завтрашняя дата)
 */
export const isTrainingPublished = (publishAt: string | null | undefined): boolean => {
    if (!publishAt) return false;

    const now = new Date();
    let publishDate: Date;

    // Поддержка нового формата: YYYY-MM-DD HH:MM (локальное время)
    if (publishAt.includes(' ')) {
        const [dateStr, timeStr] = publishAt.split(' ');
        publishDate = new Date(`${dateStr}T${timeStr}:00`);
    } else {
        // Старый формат: ISO string (UTC время) - но время должно быть абсолютным
        publishDate = new Date(publishAt);
    }

    // Сравниваем даты (год, месяц, день) - используем UTC дату из publish_at, но сравниваем с локальной датой
    const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let publishDateOnly: Date;

    if (publishAt.includes(' ')) {
        // Новый формат - локальная дата
        publishDateOnly = new Date(publishDate.getFullYear(), publishDate.getMonth(), publishDate.getDate());
    } else {
        // Старый формат - берем UTC дату, но трактуем как локальную
        publishDateOnly = new Date(publishDate.getUTCFullYear(), publishDate.getUTCMonth(), publishDate.getUTCDate());
    }

    // Если дата публикации в будущем - тренировка еще не опубликована
    if (publishDateOnly > nowDate) {
        return false;
    }

    // Если дата публикации в прошлом - тренировка уже опубликована
    if (publishDateOnly < nowDate) {
        return true;
    }

    // Если дата публикации сегодня - сравниваем время
    // Извлекаем время, используя универсальную функцию (которая извлекает UTC время из ISO строки)
    const timeStr = extractTimeFromPublishAt(publishAt);
    const [hours, minutes] = timeStr.split(':');
    const publishHours = parseInt(hours, 10);
    const publishMinutes = parseInt(minutes, 10);

    // Текущее локальное время
    const nowHours = now.getHours();
    const nowMinutes = now.getMinutes();

    // Конвертируем в минуты для удобного сравнения
    const nowTimeInMinutes = nowHours * 60 + nowMinutes;
    const publishTimeInMinutes = publishHours * 60 + publishMinutes;

    return nowTimeInMinutes >= publishTimeInMinutes;
};

/**
 * Извлекает время из строки publish_at
 * @param publishAt - время публикации в формате "YYYY-MM-DD HH:MM" или ISO string
 * @returns время в формате HH:mm
 * 
 * Примеры:
 * - "2025-08-25 14:31" -> "14:31"
 * - "2025-08-25T14:31:17.583035Z" -> "14:31" (для совместимости)
 */
export const extractTimeFromPublishAt = (publishAt: string): string => {
    if (!publishAt) return '00:00';

    // Новый формат: YYYY-MM-DD HH:MM
    if (publishAt.includes(' ')) {
        const [, timeStr] = publishAt.split(' ');
        return timeStr || '00:00';
    }

    // Старый формат: ISO string для совместимости
    const timeStr = publishAt.split('T')[1] || '';
    const [hours, minutes] = timeStr.split(':');
    return `${hours || '00'}:${minutes || '00'}`;
};

/**
 * Извлекает дату из строки publish_at
 * @param publishAt - время публикации в формате "YYYY-MM-DD HH:MM" или ISO string
 * @returns дата в формате YYYY-MM-DD
 * 
 * Примеры:
 * - "2025-08-25 14:31" -> "2025-08-25"
 * - "2025-08-25T14:31:17.583035Z" -> "2025-08-25" (для совместимости)
 */
export const extractDateFromPublishAt = (publishAt: string): string => {
    if (!publishAt) return new Date().toISOString().split('T')[0];

    // Новый формат: YYYY-MM-DD HH:MM
    if (publishAt.includes(' ')) {
        const [dateStr] = publishAt.split(' ');
        return dateStr || new Date().toISOString().split('T')[0];
    }

    // Старый формат: ISO string для совместимости
    const dateStr = publishAt.split('T')[0] || '';
    return dateStr;
};